import React, { useEffect, useState } from 'react';
import { Popover } from '@material-ui/core';
import styled from '@emotion/styled';
import { CURATED_PALETTE, findPresetIndex, evaluateHexSubmit } from '../util/routeColors';
import { normalizeColor } from '../util/colorDistance';

const Container = styled.div`
    padding: 12px;
    width: 220px;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
`;

const Swatch = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 4px;
    border: 2px solid ${(p) => (p.pressed ? '#000' : 'transparent')};
    background: ${(p) => p.color};
    cursor: pointer;
    padding: 0;
    outline: none;

    &:focus-visible {
        outline: 2px solid #1f6feb;
        outline-offset: 2px;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #e2e2e2;
    margin: 12px 0;
`;

const Label = styled.label`
    display: block;
    font-size: 12px;
    color: #4c4c4c;
    margin-bottom: 4px;
`;

const HexInput = styled.input`
    width: 100%;
    padding: 6px 8px;
    border: 1px solid ${(p) => (p.$error ? '#dc3545' : '#e2e2e2')};
    border-radius: 4px;
    font-family: monospace;
    box-sizing: border-box;
`;

const Caption = styled.div`
    font-size: 11px;
    color: ${(p) => (p.$error ? '#dc3545' : '#8d8d8d')};
    margin-top: 4px;
    min-height: 14px;
`;

const ResetLink = styled.button`
    background: none;
    border: none;
    color: #1f6feb;
    padding: 0;
    margin-top: 12px;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
`;

export default function RouteColorPicker({ anchorEl, open, currentColor, onClose, onSelect }) {
    const canonicalCurrent = normalizeColor(currentColor);
    const selectedIndex = findPresetIndex(currentColor);

    const [hexDraft, setHexDraft] = useState(canonicalCurrent || '');
    const [hexError, setHexError] = useState(null);
    const [hexWarning, setHexWarning] = useState(null);

    useEffect(() => {
        setHexDraft(canonicalCurrent || '');
        setHexError(null);
        setHexWarning(null);
    }, [canonicalCurrent, open]);

    const commitHex = () => {
        const result = evaluateHexSubmit(hexDraft);
        if (result.action === 'noop') {
            setHexError(null);
            setHexWarning(null);
            return;
        }
        if (result.action === 'error') {
            setHexError(result.message);
            setHexWarning(null);
            return;
        }
        // commit
        setHexError(null);
        setHexWarning(result.warning || null);
        onSelect(result.value);
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Container onClick={(e) => e.stopPropagation()}>
                <Grid>
                    {CURATED_PALETTE.map((color, i) => (
                        <Swatch
                            key={color}
                            type="button"
                            data-testid={`route-color-preset-${color}`}
                            color={color}
                            pressed={i === selectedIndex}
                            aria-pressed={i === selectedIndex}
                            aria-label={`Use color ${color}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(color);
                            }}
                        />
                    ))}
                </Grid>
                <Divider />
                <Label htmlFor="route-color-hex">Custom hex</Label>
                <HexInput
                    id="route-color-hex"
                    type="text"
                    placeholder="#RRGGBB"
                    value={hexDraft}
                    $error={!!hexError}
                    onChange={(e) => setHexDraft(e.target.value)}
                    onBlur={commitHex}
                    onClick={(e) => e.stopPropagation()}
                />
                <Caption $error={!!hexError}>{hexError || hexWarning || ''}</Caption>
                <ResetLink type="button" onClick={() => onSelect(null)}>
                    Reset to default
                </ResetLink>
            </Container>
        </Popover>
    );
}
